import { DB_FIELD_MAX_LENGTH } from "@selector/shared/settings";
import type { JSX } from "react";
import { reduxForm, SubmissionError } from "redux-form";
import { DialogComponent } from "widgets";

import { fileNameValidation, simpleFieldValidation, urlValidation } from "@/app-utils/form.ts";
import { type ProjectFile } from "@/graphql.ts";
import type { BrowsingDirectoryFragment } from "@/modules/files/graphql/files-queries.gql.ts";
import { createProjectHyperlink, validateFileNames } from "@/redux/modules/files.ts";

import InputField, { InputReduxFormField } from "../core/redux-form/InputField.js";

interface IAddLinkDialogProps {
  onSaved?: (file: ProjectFile) => void;
  currentFolder: BrowsingDirectoryFragment;
  onClose?: () => void;
}

enum FieldName {
  name = "name",
  url = "url",
  description = "description",
}

export const AddLinkDialog = reduxForm<FieldName, IAddLinkDialogProps>({
  form: "linkUpload",
  asyncValidate: asyncValidate,
  asyncBlurFields: [FieldName.name],
  touchOnBlur: false,
})(function AddLinkDialog(props): JSX.Element {
  return (
    <DialogComponent
      title={"New Link"}
      width={580}
      key={"file-browser-widget-add-link"}
      closeFn={props.onClose}
      mainButton={{
        label: "Save Link",
        onPress: () => {
          props
            .handleSubmit(handleSubmitData)()
            .then(() => props.onClose?.());
        },
      }}
      errorCallout={{ message: props.error }}
    >
      <InputReduxFormField
        componentClass="input"
        name={FieldName.name}
        label={"Name"}
        placeholder={`Enter link name`}
        maxLength={DB_FIELD_MAX_LENGTH.RESOURCE.NAME}
        component={InputField}
        validate={[simpleFieldValidation, fileNameValidation]}
      />
      <InputReduxFormField
        componentClass="input"
        name={FieldName.url}
        label={"Link"}
        maxLength={DB_FIELD_MAX_LENGTH.RESOURCE.URL}
        type="url"
        placeholder={`Enter link or URL`}
        component={InputField}
        validate={urlValidation}
      />
      <InputReduxFormField
        componentClass="textarea"
        name={FieldName.description}
        label="Description"
        placeholder="Optional description"
        maxLength={DB_FIELD_MAX_LENGTH.RESOURCE.DESCRIPTION}
        component={InputField}
      />
    </DialogComponent>
  );
});

// Check for duplicates
async function asyncValidate(values, dispatch, props: IAddLinkDialogProps) {
  for (const key in values) {
    if (key === (FieldName.name as string)) {
      const name = values[key];
      // We don't need to give the full path as validateFileNames pull it from redux
      const existingFiles: string[] = await dispatch(
        validateFileNames(props.currentFolder.id, [{ name } as File]),
      ).catch((error) => {
        throw {
          [FieldName.name]: error.message,
        };
      });

      // if the return array is empty == no duplicates
      if (existingFiles.length > 0) {
        throw {
          [FieldName.name]: `The name "${name}" already exists.`,
        };
      }
    }
  }

  // return undefined (default) validates the current fields (values)
}

// Save the link
async function handleSubmitData(values, dispatch, props: IAddLinkDialogProps) {
  return dispatch(
    createProjectHyperlink(
      props.currentFolder.id,
      values[FieldName.name] as string,
      values[FieldName.url] as string,
      values[FieldName.description] as string,
    ),
  )
    .then((file: ProjectFile) => {
      if (props.onSaved) {
        props.onSaved(file);
      }
    })
    .catch((error) => {
      throw new SubmissionError({
        _error: error.message,
      });
    });
}
